// Clara360 Documents Service - MIT MASTER SUPABASE CLIENT!
import { masterSupabase } from '../lib/supabase-master.js';
import documentAccessService from './documentAccessService.js';
import { classifyDocument as llmClassify, routeDocument as llmRoute } from './documentClassifier.js';

class DocumentsService {
  constructor() {
    this.supabase = masterSupabase; // ✅ MASTER CLIENT VERWENDEN!
  }

  // ✅ UUID-VALIDIERUNG
  validateUUID(value) {
    if (!value || value === "" || value === "undefined" || value === "null") {
      return null;
    }
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(value) ? value : null;
  }

  // ✅ GET STATS FUNKTION
  async getStats(propertyId = null, unitId = null) {
    try {
      let query = this.supabase
        .from('documents')
        .select('*');

      if (propertyId) {
        const validPropertyId = this.validateUUID(propertyId);
        if (validPropertyId) {
          query = query.eq('property_id', validPropertyId);
        }
      }

      if (unitId) {
        const validUnitId = this.validateUUID(unitId);
        if (validUnitId) {
          query = query.eq('unit_id', validUnitId);
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      const stats = {
        total: data.length,
        byCategory: {},
        byType: {},
        recent: data.filter(doc => {
          const uploadDate = new Date(doc.upload_date || doc.created_at);
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return uploadDate > weekAgo;
        }).length,
        ocrProcessed: data.filter(doc => doc.ocr_status === 'completed').length,
        pendingReview: data.filter(doc => doc.processing_status === 'pending').length
      };

      data.forEach(doc => {
        const category = doc.category || 'uncategorized';
        stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
        
        const type = doc.file_type || 'unknown';
        stats.byType[type] = (stats.byType[type] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('❌ Get stats failed:', error);
      throw error;
    }
  }

  // ✅ STANDARD UPLOAD-FUNKTION
  async uploadDocument(file, metadata = {}) {
    try {
      const document = await documentAccessService.uploadDocument(file, {
        ...metadata,
        category: metadata.category || this.categorizeDocument(file.name),
      });
      console.log('✅ Document uploaded successfully:', document.id);
      return {
        ...document,
        file_url: document.viewer_url || null,
        public_url: null,
        access_expires_at: document.expires_at || null,
      };
    } catch (error) {
      console.error('❌ Document upload failed:', error);
      throw error;
    }
  }

  // ✅ INTELLIGENTE RECHNUNGSERFASSUNG - NUR OCR
  async uploadIntelligentInvoice(file, metadata = {}) {
    try {
      console.log('🚀 Starting intelligent invoice upload:', file.name);
      
      // 1. Standard-Upload
      const uploadResult = await this.uploadDocument(file, metadata);
      
      // 2. NUR OCR-Analyse (KEIN OPENAI!)
      let analysisResult = null;
      try {
        analysisResult = await this.analyzeInvoiceWithOCR(file);
        console.log('✅ OCR analysis completed:', analysisResult);
      } catch (ocrError) {
        console.warn('⚠️ OCR analysis failed:', ocrError);
        // Kein Fallback zu OpenAI!
      }
      
      return {
        ...uploadResult,
        analysisResult,
        intelligentProcessing: true
      };
    } catch (error) {
      console.error('❌ Intelligent invoice upload failed:', error);
      throw error;
    }
  }

  // ✅ ALIAS FÜR FRONTEND-KOMPATIBILITÄT
  async uploadInvoiceWithAnalysis(file, metadata = {}) {
    return this.uploadIntelligentInvoice(file, metadata);
  }

  // ✅ OCR-ANALYSE NUR MIT MEINEM SERVICE
  async analyzeInvoiceWithOCR(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch('/api/ocr/analyze', {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`OCR API failed: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ OCR analysis result:', result);
      
      return {
        extractedText: result.extracted_text,
        extractedAmount: result.amount,
        extractedDate: result.date,
        extractedSupplier: result.supplier_name,
        extractedCategory: result.category,
        ocrConfidence: result.confidence,
        reviewRequired: result.confidence < 0.7,
        autoApproved: result.confidence >= 0.8
      };
    } catch (error) {
      console.error('❌ OCR analysis failed:', error);
      throw error;
    }
  }

  // ✅ DOKUMENTE ABRUFEN
  async getDocuments(propertyId = null, unitId = null, tenantId = null) {
    try {
      let query = this.supabase
        .from('documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (propertyId) {
        const validPropertyId = this.validateUUID(propertyId);
        if (validPropertyId) {
          query = query.eq('property_id', validPropertyId);
        }
      }

      if (tenantId) {
        const validTenantId = this.validateUUID(tenantId);
        if (validTenantId) {
          query = query.eq('tenant_id', validTenantId);
        }
      }

      if (unitId) {
        const validUnitId = this.validateUUID(unitId);
        if (validUnitId) {
          query = query.eq('unit_id', validUnitId);
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Get documents failed:', error);
      throw error;
    }
  }

  // ✅ DOKUMENT LÖSCHEN
  async deleteDocument(documentId) {
    try {
      const validId = this.validateUUID(documentId);
      if (!validId) throw new Error('Invalid document ID');
      const result = await documentAccessService.deleteDocument(validId);
      return { success: result.deleted === true };
    } catch (error) {
      console.error('❌ Delete document failed:', error);
      throw error;
    }
  }

  async getSecureDocumentAccess(documentId, options = {}) {
    return documentAccessService.getDocumentAccess(documentId, options);
  }

  async openDocument(documentId) {
    return documentAccessService.openDocument(documentId);
  }

  // 🧠 CLASSIFIER WRAPPER
  async classifyDocument(text) {
    return llmClassify(text);
  }

  // 🔀 ROUTING WRAPPER
  routeDocument(type, confidence) {
    return llmRoute(type, confidence);
  }

  // ✅ INTELLIGENTE KATEGORISIERUNG
  categorizeDocument(filename, ocrText = '') {
    const text = (filename + ' ' + ocrText).toLowerCase();
    
    if (text.includes('rechnung') || text.includes('invoice') || text.includes('rg') || text.includes('re-')) {
      return 'Rechnung';
    }
    if (text.includes('vertrag') || text.includes('contract') || text.includes('vereinbarung')) {
      return 'Vertrag';
    }
    if (text.includes('beleg') || text.includes('receipt') || text.includes('quittung')) {
      return 'Beleg';
    }
    
    return 'Sonstiges';
  }
}

export default new DocumentsService();

